import type { InfiniteData, QueryClient, QueryKey } from "@tanstack/react-query";

/**
 * An infinite list that is OFF screen goes back to its first page when it is
 * invalidated.
 *
 * TanStack refetches an infinite query by walking every page it holds, one
 * request after another, because each page's offset comes from the one before
 * it. A Contacts list scrolled to row 1,000 is 17 pages, and anything that
 * saves a contact anywhere invalidates every list under `/api/contacts` — so
 * the next visit paid 17 serial requests, and the 17 pages stayed resident in
 * the meantime for a list nobody was looking at.
 *
 * A list ON screen is left alone: it refetches what the reader has loaded, so
 * the rows they are looking at stay where they are. Only once nothing is
 * observing it is it cut to its first page — keeping its timestamp and its
 * invalidated mark, so the next visit refetches that one page and the
 * sentinel loads the rest as the reader scrolls, exactly as a first visit
 * does. A list nobody invalidated is never touched, so Back still finds every
 * page it left.
 */

type Matcher = (queryKey: QueryKey) => boolean;

const installed = new WeakMap<QueryClient, Set<Matcher>>();

/** Register `matches` (a module-level function: one per list) on `client`. Idempotent. */
export function trimInfiniteListsWhenIdle(client: QueryClient, matches: Matcher): void {
  let matchers = installed.get(client);
  if (!matchers) {
    const set = new Set<Matcher>();
    matchers = set;
    installed.set(client, set);
    client.getQueryCache().subscribe((event) => {
      if (event.type !== "updated" || event.action.type !== "invalidate") return;
      const { query } = event;
      if (query.getObserversCount() > 0) return;
      for (const m of set) {
        if (!m(query.queryKey)) continue;
        // Out of the listener: it writes to the cache it is being told about.
        queueMicrotask(() => trimToFirstPage(client, query.queryKey));
        return;
      }
    });
  }
  matchers.add(matches);
}

/** Cut an unobserved infinite query to its first page, still stale and still invalidated. */
export function trimToFirstPage(client: QueryClient, queryKey: QueryKey): void {
  const query = client.getQueryCache().find({ queryKey, exact: true });
  if (!query || query.getObserversCount() > 0) return;
  const data = query.state.data as InfiniteData<unknown> | undefined;
  if (!data?.pages || data.pages.length <= 1) return;
  const wasInvalidated = query.state.isInvalidated;
  client.setQueryData<InfiniteData<unknown>>(
    queryKey,
    { pages: data.pages.slice(0, 1), pageParams: data.pageParams.slice(0, 1) },
    { updatedAt: query.state.dataUpdatedAt },
  );
  // setQueryData clears the invalidated mark; put it back without fetching.
  if (wasInvalidated) void client.invalidateQueries({ queryKey, exact: true, refetchType: "none" });
}
